import { useEffect, useState } from "react";

import { ArrowButton, Button, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { editProfession, getProfessionById } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { Profession, ProfessionType, ProfessionValidationScheme } from "../../models/Profession";
import { codebookRouteProfession } from "../../routes/routes";
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookKnowledgeCheckEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profession, setProfession] = useState<Profession>({ id: 0, code: "", name: "", typeId: ProfessionType.NISOO, nameEng: "" });

  useEffect(() => {
    if (!location || !location.state || !(location.state as any).professionId) {
      history.push(
        codebookRouteProfession(),
        {
          page: "list"
        }
      );
      return;
    }
    const getProfession = async () => {
      var result = await getProfessionById((location.state as any).professionId);
      setProfession(result);
      setLoading(false);
    }
    getProfession();
  }, [location, history]);


  const [errors, doValidations] = useValidation({
    data: profession,
    validationSchema: ProfessionValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (profession.id) {
      await editProfession(profession.id, profession);
    }
    navigateToList();
  }

  const cancel = () => {
    navigateToList();
  }

  const navigateToList = () => {
    history.push(
      codebookRouteProfession(),
      {
        page: "list"
      }
    );
  }

  return (
    <>
      <Row>
        <Col md={9}>
          <ArrowButton backwards ariaLabel="" onClick={navigateToList} />
          <span className={styles.backButtonCaption}>
            {t("codeBook.returnToList")}
          </span>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col md={8}>
          <h3>{t("codeBook.knowledgeCheck")}</h3>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <Button outline onClick={() => cancel()}>
            {t("common.cancel")}
          </Button>
          <Button className="ml-3" onClick={() => saveChanges()}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      {!loading &&
        <>
          <Row className="mt-5">
            <Col md={4}>
              <TextField
                name={nameof<typeof profession>("code")}
                label={t("common.code")}
                errorText={errors?.code}
                value={profession.code}
                maxLength={50}
                onChange={handleChange(setProfession)}
              />
            </Col>
            <Col md={4}>
              <TextField
                name={nameof<typeof profession>("name")}
                label={t("common.name")}
                errorText={errors?.name}
                value={profession.name}
                maxLength={255}
                onChange={handleChange(setProfession)}
              />
            </Col>
            <Col md={4} >
              <TextField
                name={nameof<typeof profession>("nameEng")}
                label={t("common.nameEng")}
                errorText={errors?.nameEng}
                value={profession.nameEng}
                maxLength={255}
                onChange={handleChange(setProfession)}
              />
            </Col>
          </Row>
        </>
      }
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}